/**
 * One counter tile in a run's metric grid.
 *
 * Extracted from `runs/show.tsx` when the page grew a second grid: the scrape
 * grid counts scrape outcomes, the enrichment grid counts enrichment outcomes,
 * and the tile itself is the only thing they share.
 */
import React from "react";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export type MetricTone =
  | "sky"
  | "emerald"
  | "orange"
  | "zinc"
  | "amber"
  | "red"
  | "indigo";

const TONE: Record<MetricTone, { chip: string; ring: string }> = {
  sky: {
    chip: "bg-sky-500/10 text-sky-700 dark:text-sky-300",
    ring: "ring-sky-500/20",
  },
  emerald: {
    chip: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    ring: "ring-emerald-500/20",
  },
  orange: {
    chip: "bg-orange-500/10 text-orange-700 dark:text-orange-300",
    ring: "ring-orange-500/20",
  },
  zinc: {
    chip: "bg-zinc-500/10 text-zinc-700 dark:text-zinc-300",
    ring: "ring-zinc-500/20",
  },
  amber: {
    chip: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
    ring: "ring-amber-500/20",
  },
  red: {
    chip: "bg-red-500/10 text-red-700 dark:text-red-300",
    ring: "ring-red-500/20",
  },
  indigo: {
    chip: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300",
    ring: "ring-indigo-500/20",
  },
};

export interface MetricCardProps {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  tone: MetricTone;
  share?: number | null;
  hint?: string;
  onClick?: () => void;
  active?: boolean;
}

export const MetricCard = ({
  label,
  value,
  icon,
  tone,
  share,
  hint,
  onClick,
  active,
}: MetricCardProps) => {
  const t = TONE[tone];
  return (
    <Card
      className={cn(
        "overflow-hidden",
        onClick &&
          "cursor-pointer transition-colors hover:border-foreground/20 hover:bg-accent/40",
        active && "border-foreground/40 bg-accent/60 ring-1 ring-foreground/20",
      )}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-pressed={onClick ? Boolean(active) : undefined}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <CardContent className="p-3 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span
            className={cn(
              "h-7 w-7 rounded-md inline-flex items-center justify-center ring-1",
              t.chip,
              t.ring,
            )}
          >
            {icon}
          </span>
          <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {label}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-2xl font-semibold font-mono tabular-nums leading-none">
            {value}
          </span>
          {share != null && (
            <span className="text-[11px] font-mono text-muted-foreground tabular-nums">
              {share.toFixed(0)}%
            </span>
          )}
        </div>
        {hint && (
          <span className="text-[10px] text-muted-foreground truncate">
            {hint}
          </span>
        )}
      </CardContent>
    </Card>
  );
};

export const shareOf = (n: number, total: number) =>
  total > 0 ? (n / total) * 100 : null;
